// @ts-strict

import { ChangeDetectorRef, Component, HostListener, Input, OnChanges, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CountriesData } from 'countries-map';

// TODO Remove when endpoint will be provided
import { countries } from '@app/core/helpers/countries';
import { StatisticsSummaryService } from '@services/statistics/statistics-summary.service';
import { StatisticsMapItem } from '@pages/statistics/shared/interfaces/statistics';

@Component({
  selector: 'app-statistics-maps-graph',
  templateUrl: './statistics-maps-graph.component.html',
  styleUrls: ['./statistics-maps-graph.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StatisticsMapsGraphComponent implements OnInit {
  @Input() mapData: StatisticsMapItem[] = [];
  @Input() mapCompareData: StatisticsMapItem[] = [];
  @Input() compareMode = false;

  public countriesData: CountriesData = {};
  public isCountryDetailsVisible = false;
  public selectedCountry?: StatisticsMapItem;
  public selectedCompareCountry?: StatisticsMapItem;
  public clickedCountry = '';

  private readonly BASE_COLOR = [69, 191, 82];
  private readonly ERROR_COLOR = [254, 60, 37];
  private readonly MIN_ALPHA = 0.1;
  private readonly TOOLTIP_WIDTH = 300;
  private readonly OFFSET = 30;

  private countryList: string[] = [];
  private currentMousePosition = { xPos: 0, yPos: 0 };
  private greatestVisitsNumber = 0;

  private mouseMoveTimeout: NodeJS.Timeout | null = null;

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    // Throttle mouse move events
    if (this.mouseMoveTimeout) return;

    this.mouseMoveTimeout = setTimeout(() => {
      this.currentMousePosition = {
        xPos: event.clientX,
        yPos: event.clientY
      };
      this.mouseMoveTimeout = null;
    }, 16); // ~60fps
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private readonly statistics: StatisticsSummaryService
  ) {}

  ngOnInit(): void {
    this.countryList = Object.keys(countries);
    this.countryList.forEach(country => {
      this.countriesData[`${country}`] = { value: 0 };
    });
    this.statistics.compareMode.subscribe(res => {
      this.compareMode = res.compareMode;

      if (this.compareMode) {
        this.mapGraphData();
      }
    });
    this.clearSelectedCountry();

    this.calculateGreatestVisitsNumber();

    this.mapGraphData();
  }

  public select(country: { selected: boolean; value: number; country: string }): void {
    this.selectedCountry = undefined;
    this.setCurrentSelectedCountry(country.country);
    if (this.selectedCountry) {
      this.isCountryDetailsVisible = true;
      this.positionDetailsContainer();

      this.clickedCountry = country.country.toLowerCase();
      const fillCountry = document.getElementById(this.clickedCountry)!;

      if (this.clickedCountry) {
        this.mapGraphData();
      }

      if (this.compareMode) {
        const currentEarnings = this.mapData.find(o => o.country_code === country.country.toLowerCase());
        const compareEarnings = this.mapCompareData.find(o => o.country_code === country.country.toLowerCase());

        let earningsDifference = 0;

        if (currentEarnings && compareEarnings) {
          earningsDifference = this.calculateEarningsDifference(currentEarnings, compareEarnings);
        }

        if (earningsDifference < 0) {
          fillCountry.setAttribute('style', 'fill: rgb(254, 60, 37) !important');
        } else {
          fillCountry.setAttribute('style', 'fill: rgb(69, 191, 82) !important');
        }
      } else {
        fillCountry.setAttribute('style', 'fill: rgb(69, 191, 82) !important');
      }
    } else {
      const fillCountry = document.getElementById(this.clickedCountry);
      if (fillCountry) {
        fillCountry.setAttribute('style', 'fill: rgb(255, 255, 255) !important');
      }
      if (this.compareMode) {
        this.mapGraphData();
      }
      this.closeDetails();
    }
  }

  public closeDetails(): void {
    this.isCountryDetailsVisible = false;
    this.selectedCountry = undefined;
    this.cdr.detectChanges();
    this.mapGraphData();
  }

  private mapGraphData(): void {
    this.mapData.forEach(countryData => {
      this.highLightSelectedCountry(countryData);
    });
  }

  private setCurrentSelectedCountry(country_id: string): void {
    if (country_id) {
      this.mapData.forEach(item => {
        if (item.country_code.toLowerCase() === country_id.toLowerCase()) {
          this.selectedCountry = item;
        }
      });
      if (this.mapCompareData?.length > 0 && this.compareMode) {
        this.mapCompareData.forEach(item => {
          if (item.country_code.toLowerCase() === country_id.toLowerCase()) {
            this.selectedCompareCountry = item;
          }
        });
      }
    }
  }

  private calculateEarningsDifference = (selectedCountry: StatisticsMapItem, selectedCompareCountry: StatisticsMapItem): number => {
    if (!selectedCompareCountry) return 0;

    const { earnings_status_payment_waiting, earnings_status_to_payment, earnings_status_wait } = selectedCountry;
    const {
      earnings_status_payment_waiting: comparePaymentWaiting,
      earnings_status_to_payment: compareToPayment,
      earnings_status_wait: compareWait
    } = selectedCompareCountry;

    const selectedCountryEarnings =
      parseFloat(earnings_status_payment_waiting ?? '0.00') + parseFloat(earnings_status_wait ?? '0.00') + parseFloat(earnings_status_to_payment ?? '0.00');
    const selectedCompareCountryEarnings =
      parseFloat(comparePaymentWaiting ?? '0.00') + parseFloat(compareToPayment ?? '0.00') + parseFloat(compareWait ?? '0.00');

    const earningsDifference = selectedCountryEarnings - selectedCompareCountryEarnings;

    return earningsDifference;
  };

  private positionDetailsContainer(): void {
    const detailsContainer = document.getElementById('detailsContainer');
    const mapContainer = document.getElementById('myMapContainer')?.getBoundingClientRect();
    const mapOuterContainer = document.getElementById('maps-graph-container');

    if (!detailsContainer || !mapContainer || !mapOuterContainer) return;

    const detailsContainerHeight = document.getElementById('countryDetailsContainer')?.clientHeight || 0;

    const position = this.calculateOptimalPosition(detailsContainerHeight, mapContainer, mapOuterContainer);

    requestAnimationFrame(() => {
      detailsContainer.style.left = `${position.left}px`;
      detailsContainer.style.top = `${position.top}px`;
    });
  }

  private calculateOptimalPosition(height: number, mapBounds: DOMRect, container: HTMLElement): { left: number; top: number } {
    const { xPos, yPos } = this.currentMousePosition;
    let left = xPos + this.OFFSET - mapBounds.left;
    let top = yPos - mapBounds.top + this.OFFSET;

    // Horizontal position
    if (left + this.TOOLTIP_WIDTH > container.clientWidth) {
      left = Math.max(10, xPos - this.OFFSET - mapBounds.left - this.TOOLTIP_WIDTH);
    }
    if (xPos < this.TOOLTIP_WIDTH + 20) {
      left = xPos + 50;
    }

    // Vertical position
    const containerBottom = container.offsetTop + container.clientHeight;
    if (yPos + window.scrollY - mapBounds.top + this.OFFSET + height > containerBottom) {
      top = Math.min(250, yPos - mapBounds.top - 0.3 * height);
    }
    if (yPos < height + 20) {
      top = yPos - (mapBounds.top - 0);
    }

    return { left, top };
  }

  private calculateCountryColor(visits: number, isError = false): string {
    if (isError) {
      return `rgb(${this.ERROR_COLOR.join(',')})`;
    }

    const alpha = this.MIN_ALPHA + ((1 - this.MIN_ALPHA) * Math.log(visits + 1)) / Math.log(this.greatestVisitsNumber + 1);

    return `rgba(${this.BASE_COLOR.join(',')}, ${alpha})`;
  }

  private highLightSelectedCountry(country: StatisticsMapItem): void {
    const { country_code, visits } = country;
    const selectedCountry = document.getElementById(country_code.toLowerCase());

    if (!selectedCountry) return;

    const isCompareMode = this.compareMode && this.mapCompareData?.length;

    if (isCompareMode) {
      this.handleCompareMode(country, selectedCountry);
    } else {
      const color = this.calculateCountryColor(visits ?? 0);
      this.updateCountryStyle(selectedCountry, color);
    }

    this.cdr.detectChanges();
  }

  private handleCompareMode(country: StatisticsMapItem, element: HTMLElement): void {
    const compareData = this.mapCompareData?.find(c => c.country_code === country.country_code);
    if (!compareData) return;

    const earningsDifference = this.calculateEarningsDifference(country, compareData);
    const color = this.calculateCountryColor(country.visits ?? 0, earningsDifference < 0);

    this.updateCountryStyle(element, color, earningsDifference < 0);
  }

  private updateCountryStyle(element: HTMLElement, color: string, isError = false): void {
    requestAnimationFrame(() => {
      element.setAttribute('style', `fill: ${color} !important`);
      element.style.width = '0.3%';
      element.style.stroke = isError ? `rgb(${this.ERROR_COLOR.join(',')})` : `rgb(${this.BASE_COLOR.join(',')})`;

      element.querySelectorAll('path').forEach(path => {
        path.style.stroke = element.style.stroke;
      });
    });
  }

  private clearSelectedCountry(): any {
    for (const key of Object.keys(this.countriesData)) {
      const temp = document.getElementById(key.toLowerCase());
      if (temp) {
        temp.setAttribute('style', 'fill: rgb(255, 255, 255) !important');
        temp.style.width = `0.1%`;
        temp.style.stroke = `rgb(135, 145, 156)`;
      }
    }
    this.cdr.detectChanges();
    return;
  }

  private calculateGreatestVisitsNumber = () => {
    this.greatestVisitsNumber = Math.max(...this.mapData.map(({ visits }) => visits ?? 0));
  };
}
